/**
 * Per-action financial models for scenario impact (SE-7002, FC-1.1).
 *
 * Sign, Sell, Loan and Promote are FOUR DIFFERENT financial models and the
 * ticket is explicit that one must not be applied to all four:
 *
 * - **Sign** — a transfer-fee model. Fee and agent fee are capitalised and
 *   amortised straight-line across the contract; the signing bonus folds into
 *   the wage line rather than being capitalised (PRD §8).
 * - **Sell** — removes the player from the squad entirely, so his whole
 *   baseline (wage, remaining amortisation, remaining book value) comes off.
 *   The sale price itself is a capital-gain line, not one of the three deltas
 *   the panel renders, so it does not appear here.
 * - **Loan** — a wage-share model. The club keeps the registration and the
 *   book value; only the agreed share of the wage moves, prorated across the
 *   part of the year the loan actually covers. A loan with NO recorded share
 *   is a third state, neither 0 nor 100: it still moves the registration, but
 *   it contributes no wage figure and is disclosed as unrecorded (SE-7411).
 * - **Promote** — a contract-terms model. No fee exists, so nothing is
 *   capitalised and nothing amortises; only the new wage lands.
 *
 * Everything here is pure so the models can be tested directly rather than
 * through a rendered modal.
 * @module features/financial-compliance/domain/actionFinancials
 */

/** Months in a year — the divisor every annualisation here shares. */
const MONTHS_PER_YEAR = 12;

/** A covered share is a whole-percent value in `[0, FULL_COVERED_SHARE]`. */
const FULL_COVERED_SHARE_PERCENT = 100;

/**
 * The fraction of a loaned player's wage that leaves the LENDING club's bill.
 *
 * DIRECTION — do not "correct" this against a field description.
 * `coveredSharePercent` is the percent of the wage the **BORROWING** club
 * covers. On a LOAN_OUT (the only loan direction Squad Planning emits) this
 * club is the lender, so it is relieved of `wage × share / 100` and retains
 * `wage × (100 − share) / 100`. The deployed dev GraphQL schema served the
 * INVERTED description ("percent THIS CLUB carries") until backend **SE-7403**
 * corrected it; a reader who trusts a stale doc string, an IDE tooltip, or an
 * older introspection will try to flip this and reintroduce SE-7422.
 *
 * ABSENCE — absent or out of range yields `null`, never a number. 100 is the
 * assumption SE-7422 exists to remove and 0 is an equally silent guess; both
 * put a figure nobody entered in front of a user. `null` means "no recorded
 * share", which is the same honesty the compliance engine applies by staying
 * PARTIAL (SE-7324, SE-7158).
 * @param coveredSharePercent - The persisted covered share, 0–100.
 * @returns The relief fraction 0–1, or null when no usable share is recorded.
 */
export const resolveCoveredShareFraction = (
  coveredSharePercent: number | null | undefined
): number | null => {
  if (coveredSharePercent == null || !Number.isFinite(coveredSharePercent))
    return null;
  if (
    coveredSharePercent < 0 ||
    coveredSharePercent > FULL_COVERED_SHARE_PERCENT
  )
    return null;
  return coveredSharePercent / FULL_COVERED_SHARE_PERCENT;
};

/**
 * The annual wage a loan-out actually removes from the lending club's bill.
 *
 * Every scenario surface that shows a wage delta routes through this so the
 * four of them cannot drift apart again — that drift is exactly SE-7422.
 * See {@link resolveCoveredShareFraction} for the direction and the reason an
 * unrecorded share is `null` rather than a number.
 * @param playerWage - The player's effective annual wage.
 * @param coveredSharePercent - The loan's persisted covered share, 0–100.
 * @returns The wage removed, or null when the loan has no recorded share.
 */
export const loanWageRelief = (
  playerWage: number,
  coveredSharePercent: number | null | undefined
): number | null => {
  const fraction = resolveCoveredShareFraction(coveredSharePercent);
  return fraction == null ? null : playerWage * fraction;
};

/** Deal terms for a Sign action. */
export interface SignTerms {
  /** Discriminator. */
  readonly kind: "Sign";
  /** Transfer fee paid, in currency units. */
  readonly transferFee: number | null;
  /** Annual gross salary. */
  readonly annualSalary: number | null;
  /** Contract length in months. */
  readonly durationMonths: number | null;
  /** One-off signing bonus; folded into the wage line, not capitalised. */
  readonly signingBonus: number | null;
  /** Agent fee; capitalised alongside the transfer fee. */
  readonly agentFee: number | null;
}

/** Deal terms for a Sell action. */
export interface SellTerms {
  /** Discriminator. */
  readonly kind: "Sell";
  /** Agreed sale price. */
  readonly salePrice: number | null;
}

/** Deal terms for a Loan action. */
export interface LoanTerms {
  /** Discriminator. */
  readonly kind: "Loan";
  /** Loan fee received. */
  readonly loanFee: number | null;
  /** Loan length in months. */
  readonly durationMonths: number | null;
  /** Percentage of the wage the receiving club takes on (0–100). */
  readonly wageSharePercent: number | null;
}

/** Deal terms for a Promote action. */
export interface PromoteTerms {
  /** Discriminator. */
  readonly kind: "Promote";
  /** Annual gross salary on the new contract. */
  readonly annualSalary: number | null;
  /** Contract length in months. */
  readonly durationMonths: number | null;
}

/** Terms for any of the four scenario actions. */
export type ActionTerms = SignTerms | SellTerms | LoanTerms | PromoteTerms;

/** What one action adds to (or removes from) the club's financial aggregate. */
export interface ActionFinancialContribution {
  /** Change to the annual wage bill. */
  readonly wagePerYear: number;
  /** Change to the annual amortisation charge. */
  readonly amortisationPerYear: number;
  /** Change to the carrying value of registrations. */
  readonly netBookValue: number;
  /** Change to the registered squad size, in players. */
  readonly squadSize: number;
}

/**
 * What a player already costs the club today.
 *
 * Sell and Loan are RELIEF models — they can only be quantified against the
 * player's existing wage and book value, which the modal does not hold. The
 * adapter supplies this; an unknown player yields zeroes rather than a guess.
 */
export interface PlayerBaselineFinancials {
  /** The player's current annual wage. */
  readonly wagePerYear: number;
  /** The player's current annual amortisation charge. */
  readonly amortisationPerYear: number;
  /** The player's remaining carrying value. */
  readonly netBookValue: number;
}

/** A player with no known baseline contributes no relief — never a guess. */
export const EMPTY_PLAYER_BASELINE: PlayerBaselineFinancials = {
  wagePerYear: 0,
  amortisationPerYear: 0,
  netBookValue: 0,
};

/** Nothing entered yet, or terms that cannot be annualised. */
const NO_CONTRIBUTION: ActionFinancialContribution = {
  wagePerYear: 0,
  amortisationPerYear: 0,
  netBookValue: 0,
  squadSize: 0,
};

/**
 * A loan whose covered share nobody recorded (SE-7411).
 *
 * Note what is and is not zero here, because the difference is the whole
 * point. `squadSize: -1` is a FACT — the player leaves the registered squad
 * whatever the wage arrangement, and nobody is uncertain about it. The zero
 * `wagePerYear` is not a claim that the loan relieves nothing; it is this
 * aggregate declining to put a wage figure it does not have into a total, and
 * it is only honest because {@link ActionFinancialContribution} never travels
 * without the disclosure the surfaces render beside it
 * (`formatUnrecordedCoveredShareNote`). A zero that is never disclosed is the
 * same lie as an assumed 100.
 */
const UNRECORDED_LOAN_SHARE_CONTRIBUTION: ActionFinancialContribution = {
  wagePerYear: 0,
  amortisationPerYear: 0,
  netBookValue: 0,
  squadSize: -1,
};

/**
 * Coerce a nullable money/number field to a finite number.
 * @param value - The nullable field.
 * @returns The value, or zero when it is null or not finite.
 */
const num = (value: number | null): number =>
  value != null && Number.isFinite(value) ? value : 0;

/**
 * Negate without producing `-0`.
 *
 * Relief models negate a baseline, and a zero baseline would otherwise yield
 * `-0` — which compares unequal to `0` under strict structural equality and
 * would surface as a sign on a delta that never moved.
 *
 * Exported because the scenario surfaces negate the same relief figures and
 * were reproducing the `-0` this exists to prevent (SE-7422). Re-deriving this
 * rule per surface instead of importing it is the pattern that produced
 * SE-7422 in the first place.
 * @param value - The value to negate.
 * @returns The negated value, with `-0` normalised to `0`.
 */
export const negate = (value: number): number => (value === 0 ? 0 : -value);

/**
 * Normalise `-0` to `0`, for the same reason {@link negate} exists.
 * @param value - A computed difference.
 * @returns The value, with `-0` normalised to `0`.
 */
const zeroSafe = (value: number): number => (value === 0 ? 0 : value);

/**
 * Whether a duration in months can be annualised at all.
 * @param months - The duration in months.
 * @returns True when the duration is a positive finite number.
 */
const hasPositiveDuration = (months: number | null): boolean =>
  months != null && Number.isFinite(months) && months > 0;

/**
 * Whether the terms are complete enough to compute an impact.
 *
 * This is the gate between the panel's empty state and its verdict state. It
 * is deliberately per-action: a Sign with a fee but no salary or contract
 * length cannot be annualised, while a Sell needs only a price.
 * @param terms - The action's current draft terms.
 * @returns True when an impact can be computed.
 */
export const hasSufficientTerms = (terms: ActionTerms): boolean => {
  switch (terms.kind) {
    // Sign and Promote are different MODELS but share a readiness condition:
    // both annualise a salary across a contract, so neither can be computed
    // without both numbers. Their contributions diverge in `contributionFor`.
    case "Sign":
    case "Promote":
      return (
        num(terms.annualSalary) > 0 && hasPositiveDuration(terms.durationMonths)
      );
    case "Sell":
      return num(terms.salePrice) > 0;
    // A loan needs a duration and a covered share. The fee may be absent (a
    // free loan is still a loan), but a blank share is not a share of zero:
    // computing at zero relief would be the "assume a number nobody entered"
    // failure the covered-share field exists to prevent (SE-7324).
    case "Loan":
      return (
        hasPositiveDuration(terms.durationMonths) &&
        terms.wageSharePercent != null
      );
    default:
      return false;
  }
};

/**
 * A loan that is real enough to contribute, whatever its share situation.
 * @param terms - The action's terms.
 * @returns True for a Loan with a usable duration.
 */
const isLoanWithDuration = (terms: ActionTerms): terms is LoanTerms =>
  terms.kind === "Loan" && hasPositiveDuration(terms.durationMonths);

/**
 * Whether these terms are a staged loan whose covered share nobody recorded.
 *
 * This is the THIRD state, and keeping it distinct from the other two is the
 * point of SE-7411:
 * - a recorded share of 0 — the borrowing club pays nothing, the whole wage
 *   stays here. A real, entered value. NOT this.
 * - a recorded share of N — relief is `wage × N / 100`. NOT this.
 * - no recorded share at all — a loan persisted before SE-7324 added the
 *   field. Nobody entered anything, so no relief figure exists to report.
 *   THIS.
 *
 * Only `null` qualifies. An out-of-range share is a recorded-but-wrong value
 * and stays with the loan model's clamp, which is a separate,
 * deliberately-tested decision — see the ABSENCE note there.
 * @param terms - The action's terms.
 * @returns True when a loan's wage relief is unknown rather than known.
 */
export const hasUnrecordedLoanShare = (terms: ActionTerms): boolean =>
  isLoanWithDuration(terms) && terms.wageSharePercent == null;

/**
 * Count the loans among a set of actions whose covered share is unrecorded.
 *
 * Exported so a surface renders the disclosure from the same predicate the
 * aggregate excluded them by — a surface that recounts them its own way is how
 * a figure and its footnote drift apart.
 * @param terms - Every action being aggregated.
 * @returns How many of them are loans with no recorded covered share.
 */
export const countUnrecordedLoanShares = (
  terms: readonly ActionTerms[]
): number => terms.filter(hasUnrecordedLoanShare).length;

/**
 * Sign: capitalise fee + agent fee, amortise straight-line, fold the bonus.
 * @param terms - The signing's terms.
 * @returns The signing's contribution to the aggregate.
 */
const signContribution = (terms: SignTerms): ActionFinancialContribution => {
  const years = num(terms.durationMonths) / MONTHS_PER_YEAR;
  const capitalised = num(terms.transferFee) + num(terms.agentFee);
  return {
    wagePerYear: num(terms.annualSalary) + num(terms.signingBonus) / years,
    amortisationPerYear: capitalised / years,
    netBookValue: capitalised,
    squadSize: 1,
  };
};

/**
 * Promote: contract terms only — no fee exists, so nothing amortises.
 *
 * The wage moves by the RAISE, not by the new salary (SE-8794). A promoted
 * player is already on the payroll, so the club's cost — and the Squad Cost
 * Ratio in the same panel — moves by `new salary − current wage`. Reporting
 * the whole new salary made "+ €10.0M" sit above a ratio that moved by €5M.
 *
 * A player with no baseline (none supplied, or no wage on record) is read as
 * paid nothing today, so the whole new salary is the movement — the same
 * answer as before this fix, and the only one available without a figure.
 * The subtraction goes through {@link negate}'s rule: an unchanged salary
 * yields `0`, never `-0`.
 * @param terms - The promotion's terms.
 * @param baseline - What the player costs the club today.
 * @returns The promotion's contribution to the aggregate.
 */
const promoteContribution = (
  terms: PromoteTerms,
  baseline: PlayerBaselineFinancials
): ActionFinancialContribution => ({
  wagePerYear: zeroSafe(num(terms.annualSalary) - baseline.wagePerYear),
  amortisationPerYear: 0,
  netBookValue: 0,
  squadSize: 1,
});

/**
 * Sell: the player leaves, so his entire baseline comes off the aggregate.
 * @param baseline - What the player costs the club today.
 * @returns The sale's contribution to the aggregate.
 */
const sellContribution = (
  baseline: PlayerBaselineFinancials
): ActionFinancialContribution => ({
  wagePerYear: negate(baseline.wagePerYear),
  amortisationPerYear: negate(baseline.amortisationPerYear),
  netBookValue: negate(baseline.netBookValue),
  squadSize: -1,
});

/**
 * Loan: only the agreed wage share moves, prorated across the loan window.
 *
 * The registration and the book value stay with the parent club, which is
 * precisely what makes this a different model from Sell — a loan that is
 * modelled as a sale understates the club's carrying value.
 *
 * DIRECTION — `wageSharePercent` is the share the BORROWING club covers, so it
 * is the lender's RELIEF fraction and is used verbatim: no complement, no
 * rescaling. The dev GraphQL schema described the field the other way round
 * ("percent THIS CLUB carries") until backend **SE-7403** fixed it, so anyone
 * reading the field description instead of this comment will conclude the
 * formula is inverted and try to "fix" it. It is not. See
 * {@link resolveCoveredShareFraction} (SE-7422) for the same premise.
 *
 * ABSENCE — an ABSENT share and an OUT-OF-RANGE share are handled
 * differently here, and the asymmetry is deliberate rather than an oversight.
 * A `null` share means nobody recorded one, so no relief is claimed and the
 * loan is reported as unrecorded (SE-7411) — it is emphatically not read as
 * `num(null) === 0`, which would assert that the borrowing club covers nothing
 * and that the whole wage stays here. An out-of-range share is a recorded
 * value that cannot be true, and it is CLAMPED into range, because the modal
 * validates 0–100 so the only way to reach it is a persisted value that was
 * already wrong. {@link resolveCoveredShareFraction} is stricter still and
 * treats both as unrecorded; do not "unify" the two by making the surfaces
 * clamp — clamping answers with a number nobody entered, which is what SE-7422
 * exists to stop.
 * @param terms - The loan's terms.
 * @param baseline - What the player costs the club today.
 * @returns The loan's contribution to the aggregate.
 */
const loanContribution = (
  terms: LoanTerms,
  baseline: PlayerBaselineFinancials
): ActionFinancialContribution => {
  if (terms.wageSharePercent == null) return UNRECORDED_LOAN_SHARE_CONTRIBUTION;
  const share =
    Math.min(Math.max(num(terms.wageSharePercent), 0), 100) /
    FULL_COVERED_SHARE_PERCENT;
  const coveredMonths = Math.min(num(terms.durationMonths), MONTHS_PER_YEAR);
  const relief =
    baseline.wagePerYear * share * (coveredMonths / MONTHS_PER_YEAR);
  return {
    wagePerYear: negate(relief),
    amortisationPerYear: 0,
    netBookValue: 0,
    squadSize: -1,
  };
};

/**
 * Compute one action's contribution to the club's financial aggregate.
 * @param terms - The action's draft terms.
 * @param baseline - What the player costs today (Sell/Loan relief, and the
 * wage a Promote raises from).
 * @returns The contribution; all zeroes when the terms are insufficient.
 */
export const contributionFor = (
  terms: ActionTerms,
  baseline: PlayerBaselineFinancials = EMPTY_PLAYER_BASELINE
): ActionFinancialContribution => {
  // ORDER MATTERS, and it is the substance of SE-7411. A Loan is answered
  // BEFORE the sufficiency gate because {@link hasSufficientTerms} refuses a
  // loan with no recorded share — right for the modal's awaiting-input gate,
  // wrong for an ALREADY-STAGED loan, where only the wage relief is unknown.
  // Falling through to NO_CONTRIBUTION there would quietly drop the squad-size
  // change too, and that one nobody is uncertain about: pretending not to know
  // a thing you do know is not caution, it is a second wrong answer.
  if (isLoanWithDuration(terms)) return loanContribution(terms, baseline);
  if (!hasSufficientTerms(terms)) return NO_CONTRIBUTION;
  switch (terms.kind) {
    case "Sign":
      return signContribution(terms);
    case "Promote":
      return promoteContribution(terms, baseline);
    case "Sell":
      return sellContribution(baseline);
    default:
      return NO_CONTRIBUTION;
  }
};

/**
 * Sum contributions into one scenario-cumulative total.
 * @param contributions - Every staged action's contribution.
 * @returns The summed contribution.
 */
export const sumContributions = (
  contributions: readonly ActionFinancialContribution[]
): ActionFinancialContribution =>
  contributions.reduce<ActionFinancialContribution>(
    (total, one) => ({
      wagePerYear: total.wagePerYear + one.wagePerYear,
      amortisationPerYear: total.amortisationPerYear + one.amortisationPerYear,
      netBookValue: total.netBookValue + one.netBookValue,
      squadSize: total.squadSize + one.squadSize,
    }),
    NO_CONTRIBUTION
  );
